export function formatTimeStampToHumanReadableDateTimeSeconds(
  timestamp: string
): string {
  // Create a Date object from the timestamp string
  const date = new Date(timestamp);

  // Extract the components from the Date object
  const month = date.getMonth() + 1; // Months are zero-based, so we need to add 1
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2); // Get the last two digits of the year
  const hour = date.getHours();
  const minute = date.getMinutes();

  // Convert the hours to a 12-hour format
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const amPm = hour < 12 ? "AM" : "PM";

  // Prepare formatted strings for each component
  const monthStr = month.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const dayStr = day.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const hourStr = hour12.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const minuteStr = minute.toLocaleString("en-US", { minimumIntegerDigits: 2 });
  const seconds = date.getSeconds();

  // Combine the formatted strings into the final result
  return `${monthStr}/${dayStr}/${year} ${hourStr}:${minuteStr}:${seconds} ${amPm}`;
}

export function formatTimeStampToHumanReadableShortDateTime(
  timestamp: string | null | undefined
): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);
  const hour = date.getHours();
  const minute = date.getMinutes();
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const amPm = hour < 12 ? "AM" : "PM";
  const monthStr = month.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const dayStr = day.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const hourStr = hour12.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const minuteStr = minute.toLocaleString("en-US", { minimumIntegerDigits: 2 });
  return `${monthStr}/${dayStr}/${year} ${hourStr}:${minuteStr} ${amPm}`;
}

export function formatTimeStampToHumanReadableDate(timestamp: string): string {
  if (!timestamp) return "";
  // Create a Date object from the timestamp string
  const date = new Date(timestamp);

  // Extract the components from the Date object
  const month = date.getMonth() + 1; // Months are zero-based, so we need to add 1
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2); // Get the last two digits of the year
  const hour = date.getHours();
  const minute = date.getMinutes();

  // Convert the hours to a 12-hour format
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const amPm = hour < 12 ? "AM" : "PM";

  // Prepare formatted strings for each component
  const monthStr = month.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const dayStr = day.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const hourStr = hour12.toLocaleString("en-US", { minimumIntegerDigits: 1 });
  const minuteStr = minute.toLocaleString("en-US", { minimumIntegerDigits: 2 });

  // Combine the formatted strings into the final result
  return `${monthStr}/${dayStr}/${year} ${hourStr}:${minuteStr} ${amPm}`;
}

/**
 * Formats a date string to a human-readable format: MMM DD, YYYY
 * Example: "2023-04-15" becomes "Apr 15, 2023"
 *
 * @param dateString - ISO date string (YYYY-MM-DD) or any valid date string
 * @returns Formatted date string
 */
export function formatDate(dateString: string): string {
  if (!dateString) return "Unknown date";

  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    // Format the date as MMM DD, YYYY
    return date.toLocaleDateString("en-US", {
      month: "short", // "Jan", "Feb", etc.
      day: "numeric", // 1, 2, ... 31
      year: "numeric", // 2023, 2024, etc.
    });
  } catch (error) {
    return "Invalid date";
  }
}

/**
 * Returns a human-readable relative time description
 * Example: "2 days ago", "2 months ago", "Just now"
 *
 * @param dateString - ISO date string or any valid date string
 * @returns Relative time description
 */
export function getRelativeTimeFromNow(dateString: string): string {
  if (!dateString) return "Unknown time";

  try {
    const date = new Date(dateString);
    const now = new Date();

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid time";
    }

    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Just now
    if (diffInSeconds < 60) {
      return "Just now";
    }

    // Minutes ago
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${
        diffInMinutes === 1 ? "minute" : "minutes"
      } ago`;
    }

    // Hours ago
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
    }

    // Days ago
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
    }

    // Months ago
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
    }

    // Years ago
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} ${diffInYears === 1 ? "year" : "years"} ago`;
  } catch (error) {
    return "Invalid time";
  }
}

/**
 * Parses various date input formats and returns a Date object
 * Supports: MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD, MM/DD/YY
 *
 * @param input - Date string in various formats
 * @returns Date object or null if invalid
 */
export function parseDateInput(input: string): Date | null {
  if (!input || typeof input !== "string") return null;

  // Try different date formats
  const formats = [
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY
    /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // MM-DD-YYYY
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
    /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, // MM/DD/YY
  ];

  for (const format of formats) {
    const match = input.trim().match(format);
    if (match) {
      const [, month, day, year] = match;
      // Handle 2-digit years
      let fullYear = parseInt(year);
      if (fullYear < 100) {
        fullYear += fullYear < 50 ? 2000 : 1900;
      }
      const date = new Date(fullYear, parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
}

/**
 * Formats a Date object to a human-readable long format
 * Example: "September 12, 1984"
 *
 * @param date - Date object
 * @returns Formatted date string
 */
export function formatDateForDisplay(date: Date): string {
  if (!date || isNaN(date.getTime())) return "";

  return date.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Formats a Date object to a short format
 * Example: "Sep 12, 1984"
 *
 * @param date - Date object
 * @returns Formatted date string
 */
export function formatDateShort(date: Date): string {
  if (!date || isNaN(date.getTime())) return "";

  return date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Validates if a string can be parsed as a valid date
 *
 * @param input - Date string to validate
 * @returns True if valid date, false otherwise
 */
export function isValidDateInput(input: string): boolean {
  return parseDateInput(input) !== null;
}

/**
 * Normalizes a DOB value (Date or string) into an ISO string at midnight UTC (date-only semantics).
 * Accepts:
 *  - Date instance
 *  - YYYY-MM-DD string (treated as UTC date-only)
 *  - Any other parseable date string (coerced to date-only in UTC)
 * Returns null if invalid.
 */
export function normalizeDobToIso(dob: unknown): string | null {
  if (!dob) return null;
  if (dob instanceof Date && !isNaN(dob.getTime())) {
    const y = dob.getUTCFullYear();
    const m = dob.getUTCMonth();
    const d = dob.getUTCDate();
    return new Date(Date.UTC(y, m, d, 0, 0, 0, 0)).toISOString();
  }
  if (typeof dob === "string") {
    const trimmed = dob.trim();
    const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mon = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      return new Date(Date.UTC(y, mon, day, 0, 0, 0, 0)).toISOString();
    }
    const t = Date.parse(trimmed);
    if (Number.isFinite(t)) {
      const d = new Date(t);
      return new Date(
        Date.UTC(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate(),
          0,
          0,
          0,
          0
        )
      ).toISOString();
    }
  }
  return null;
}
